import React, { useState } from 'react';
import {
  Button,
  Icon,
  InputField,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  useDebounceForFunctions,
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../../shared/hooks';
import { type RootState } from '../../../../../../shared/types';

import './FUEInventoryControls.css';

interface FUEInventoryControlsProps {
  onSearch?: (query: string) => void;
  onExport?: () => void;
  onRunStarSimulation?: () => void;
  isFilterBarOpen: boolean;
  onToggleFilter: () => void;
  hasActiveFilters: boolean;
  isExporting?: boolean;
}

/**
 * Controls bar for the FUE Inventory tab providing search, filter toggle,
 * and action buttons for running STAR simulations and exporting data.
 */
function FUEInventoryControls({
  onSearch = undefined,
  onExport = undefined,
  onRunStarSimulation = undefined,
  isFilterBarOpen,
  onToggleFilter,
  hasActiveFilters,
  isExporting = false,
}: FUEInventoryControlsProps): React.ReactElement {
  const localize = useInvertedLocalize();
  const [searchQuery, setSearchQuery] = useState('');

  // Export progress state from Redux
  const { activeExport, exportProgress, exportStatus } = useTypedSelector(
    (state: RootState) =>
      state.aag.licenseExport ?? {
        activeExport: null,
        exportProgress: 0,
        exportStatus: null,
      }
  );

  const hasActiveExport = Boolean(activeExport);
  const isExportComplete =
    exportProgress >= 100 || exportStatus === 'COMPLETED';

  const debouncedOnSearch = useDebounceForFunctions(
    (query: string) => onSearch?.(query),
    400
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const query = e.target.value;

    setSearchQuery(query);
    debouncedOnSearch(query);
  };

  return (
    <div className='LicenseManagement-FUEInventoryControls'>
      <div className='LicenseManagement-FUEInventoryControls-topRow'>
        <div className='LicenseManagement-FUEInventoryControls-leftAlign'>
          <InputField
            name='fue-inventory-search'
            kind='search'
            size='medium'
            placeholder='Search by name or description'
            value={searchQuery}
            setValue={setSearchQuery}
            onChange={handleSearchChange}
            prefixIcon={
              <Icon
                kind='Search'
                size='small'
                className='LicenseManagement-FUEInventoryControls-searchIcon'
              />
            }
            className='LicenseManagement-FUEInventoryControls-searchInput'
          />

          <div className='LicenseManagement-FUEInventoryControls-filterButtonWrapper'>
            <Button
              className={`LicenseManagement-FUEInventoryControls-filterButton${
                isFilterBarOpen
                  ? ' LicenseManagement-FUEInventoryControls-filterButton--active'
                  : ''
              }`}
              leftIcon={
                <Icon
                  kind='Filter'
                  className='LicenseManagement-FUEInventoryControls-filterIcon'
                  size='small'
                />
              }
              onClick={onToggleFilter}>
              <Typography
                className='LicenseManagement-FUEInventoryControls-filterLabel'
                kind='body2-bold'>
                {localize('Filter')}
              </Typography>
            </Button>
            {hasActiveFilters && (
              <span className='LicenseManagement-FUEInventoryControls-badge' />
            )}
          </div>
        </div>

        <div className='LicenseManagement-FUEInventoryControls-actions'>
          <Button
            kind='filled'
            size='small'
            onClick={onRunStarSimulation}
            leftIcon={
              <span className='LicenseManagement-FUEInventoryControls-playIcon' />
            }
            className='LicenseManagement-FUEInventoryControls-starSimBtn'>
            {localize('Run STAR Simulation')}
          </Button>
          {hasActiveExport ||
          (exportProgress > 0 && exportProgress < 100) ||
          isExportComplete ? (
            <Tooltip
              text={localize('An export is already in progress')}
              placement='top'
              isPortalEnabled
              trigger={
                <div className='LicenseManagement-FUEInventoryControls-exportBtnWrapper'>
                  <Button
                    kind='outlined'
                    size='small'
                    onClick={onExport}
                    leftIcon={
                      isExportComplete ? (
                        <Icon kind='Check' size='small' />
                      ) : (
                        <Icon kind='Download' size='small' />
                      )
                    }
                    isExtendedLoading={
                      hasActiveExport ||
                      (exportProgress > 0 && exportProgress < 100)
                    }
                    isSuccess={isExportComplete}
                    extendedLoadingPercentValue={exportProgress}
                    loaderText={`${localize('Exporting')} (${exportProgress}%)`}
                    successText={localize('Exported')}
                    className={cx(
                      (hasActiveExport ||
                        (exportProgress > 0 && exportProgress < 100)) &&
                        'LicenseManagement-FUEInventoryControls-exportingButton'
                    )}>
                    {localize('Export')}
                  </Button>
                </div>
              }
            />
          ) : (
            <Button
              kind='outlined'
              size='small'
              onClick={onExport}
              leftIcon={<Icon kind='Download' size='small' />}
              isDisabled={isExporting}>
              {isExporting ? localize('Exporting...') : localize('Export')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

FUEInventoryControls.defaultProps = {
  onSearch: undefined,
  onExport: undefined,
  onRunStarSimulation: undefined,
  isExporting: false,
};

export default FUEInventoryControls;
